<template>
  <div>
    <errors :error-list="serverErrors"></errors>

    <h4 class="card-title mb-3">My Open Incident Reports</h4>

    <!-- Filters -->
    <div class="row mb-3">
      <div class="col-sm-4">
        <div class="search-box mr-2 mb-2 d-inline-block">
          <div class="position-relative">
            <input v-model="searchQuery" type="text" class="form-control" placeholder="Search..."/>
            <i class="bx bx-search-alt search-icon"></i>
          </div>
        </div>
      </div>
      <div class="col-sm-8 text-right">
        <sort-by-filters :filters="filters"></sort-by-filters>
        <button type="button" class="btn btn-itg-dark ml-3"
          @click="downloadAllReports"
          :disabled="!incidentReportResults.length">
          Download All Reports
        </button>
      </div>
    </div>
    <!-- / Filters -->

    <div class="card" v-if="isLoading||searchQueryIsDirty">
      <div class="card-body">
        <div class="text-center">
          <b-spinner class="m-2" variant="itg-dark" role="status"></b-spinner>
        </div>
      </div>
    </div>

    <!-- Cards -->
    <div v-if="!(isLoading||searchQueryIsDirty)" class="card-results-wrapper">
      <ticket-card v-for="incidentReport in incidentReportResults" :key="incidentReport.id" :ticket="incidentReport" :incident_report="incidentReport.incident_report"></ticket-card>
      <div class="card" v-if="incidentReportResults.length === 0">
        <div class="card-body">
          <b class="text-danger">No results</b>
        </div>
      </div>
    </div>
    <!--- / Cards -->

    <pagination v-if="!(isLoading||searchQueryIsDirty) && incidentReports" :data="incidentReports" :limit="8"
                @pagination-change-page="loadIncidentReports"></pagination>
  </div>
</template>

<script>
import Forms from "../../../../mixins/Forms";
import Tickets from "../../../../mixins/Tickets";
import SortByFilters from "../../tickets/index/SortByFilters";
import Dates from "../../../../mixins/Dates";
import TicketCard from "../../../../components/tickets/TicketCard";
import JSZip from 'jszip';

let _ = require('lodash');
import moment from 'moment';
import axios from 'axios';

export default {
  name: "MyTcIncidentReports",
  mixins: [Forms, Tickets, Dates],
  components: {TicketCard, SortByFilters},
  data() {
    return {
      serverErrors: [],
      isLoading: false,
      incidentReports: null,

      searchQueryIsDirty: false,
      searchQuery: '',
      filters: {
        sort_by: 'created_at',
        sort_order: 'DESC',
        results_per_page: 5,
        closed: false, // null = all, true = closed only, false = open only
        itg_included_activity: null, // null = all, true = included, false = not included
        layout: 'cards' // table or cards
      },
      
      // Date range filters
      dateRange: {
        start_date: moment().startOf('month').format('YYYY-MM-DD'),
        end_date: moment().endOf('month').format('YYYY-MM-DD')
      },
      
      // Date presets
      datePresets: [
        { label: 'This Month', start: () => moment().startOf('month'), end: () => moment().endOf('month') },
        { label: 'Last Month', start: () => moment().subtract(1, 'month').startOf('month'), end: () => moment().subtract(1, 'month').endOf('month') },
        { label: 'This Year', start: () => moment().startOf('year'), end: () => moment().endOf('year') }
      ]
    }
  },

  computed: {
    incidentReportResults() {
      if (this.incidentReports) {
        return this.incidentReports.data;
      }
      return [];
    }
  },

  methods: {
    determineSearchParams(page) {
      let params = {
        q: this.searchQuery,
        page: page,
        results_per_page: this.filters.results_per_page,
        sort_by: this.filters.sort_by,
        sort_order: this.filters.sort_order,
        // start_date: this.dateRange.start_date,
        // end_date: this.dateRange.end_date,
        incident_reports_with_me_as_travel_consultant: true
      };

      if (this.filters.closed !== null) {
        params['closed'] = this.filters.closed;
      }

      if (this.filters.itg_included_activity !== null) {
        params['itg_included_activity'] = this.filters.itg_included_activity;
      }

      return params;
    },

    loadIncidentReports: _.debounce(function (page = 1) {
      let vm = this;
      vm.serverErrors = [];
      vm.isLoading = true;
      axios.post(API_BASE + '/tickets/search-incident-reports', vm.determineSearchParams(page)).then(r => {
        vm.incidentReports = r.data;
        vm.isLoading = false;
        vm.searchQueryIsDirty = false;
      }).catch(e => {
        console.log(e);
        vm.isLoading = false;
        vm.setErrors(e);
      });
    }),

    setDatePreset(preset) {
      this.dateRange.start_date = preset.start().format('YYYY-MM-DD');
      this.dateRange.end_date = preset.end().format('YYYY-MM-DD');
      this.loadIncidentReports();
    },

    setup() {
      // Load filters from local storage if exists
      let filters = localStorage.getItem('my-tc-incident-reports-filters');
      if (filters) {
        this.filters = JSON.parse(filters);
      }

      // Load date range from local storage if exists
      let dateRange = localStorage.getItem('my-tc-incident-reports-date-range');
      if (dateRange) {
        this.dateRange = JSON.parse(dateRange);
      }

      this.loadIncidentReports();
    },

    downloadReport(ticketId) {
      // Show loading state
      this.$bvToast.toast('Downloading PDF...', {
        title: 'Info',
        variant: 'info',
        solid: true
      });

      axios.get(API_BASE + `/tickets/${ticketId}/incident-report/download-pdf`, {
        responseType: 'blob',
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `incident_report_ticket_${ticketId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        // Clean up the URL object
        window.URL.revokeObjectURL(url);
        
        // Show success message
        this.$bvToast.toast('PDF downloaded successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      }).catch(error => {
        console.error('Error downloading PDF:', error);
        this.$bvToast.toast('Failed to download PDF', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      });
    },

    async downloadAllReports() {
      if (!this.incidentReportResults.length) {
        this.$bvToast.toast('No reports to download', {
          title: 'Info',
          variant: 'info',
          solid: true
        });
        return;
      }

      // Get all tickets that have incident reports
      const ticketsWithReports = this.incidentReportResults
        .filter(report => report.incident_report);

      if (ticketsWithReports.length === 0) {
        this.$bvToast.toast('No incident reports found to download', {
          title: 'Info',
          variant: 'info',
          solid: true
        });
        return;
      }

      // Show loading state
      this.$bvToast.toast(`Preparing ${ticketsWithReports.length} reports for download...`, {
        title: 'Info',
        variant: 'info',
        solid: true
      });

      try {
        const zip = new JSZip();
        const downloadPromises = [];

        // Download all reports and add them to the zip
        for (const ticket of ticketsWithReports) {
          const promise = axios.get(API_BASE + `/tickets/${ticket.id}/incident-report/download-pdf`, {
            responseType: 'blob',
          }).then(response => {
            const fileName = `incident_report_ticket_${ticket.id}.pdf`;
            zip.file(fileName, response.data);
            return fileName;
          }).catch(error => {
            console.error(`Error downloading report for ticket ${ticket.id}:`, error);
            return null;
          });
          
          downloadPromises.push(promise);
        }

        // Wait for all downloads to complete
        const downloadedFiles = await Promise.all(downloadPromises);
        const successfulDownloads = downloadedFiles.filter(file => file !== null);

        if (successfulDownloads.length === 0) {
          this.$bvToast.toast('Failed to download any reports', {
            title: 'Error',
            variant: 'danger',
            solid: true
          });
          return;
        }

        // Generate the zip file
        this.$bvToast.toast('Generating zip file...', {
          title: 'Info',
          variant: 'info',
          solid: true
        });

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        
        // Download the zip file
        const url = window.URL.createObjectURL(zipBlob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `incident_reports_${moment().format('YYYY-MM-DD')}.zip`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        // Clean up the URL object
        window.URL.revokeObjectURL(url);
        
        // Show success message
        this.$bvToast.toast(`Successfully downloaded ${successfulDownloads.length} reports in zip file`, {
          title: 'Success',
          variant: 'success',
          solid: true
        });

      } catch (error) {
        console.error('Error creating zip file:', error);
        this.$bvToast.toast('Failed to create zip file', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    }
  },

  watch: {
    '$route': 'setup',
    searchQuery: function () {
      this.searchQueryIsDirty = true;
      this.loadIncidentReports();
    },
    dateRange: {
      handler: function () {
        this.loadIncidentReports();
        // Save to local storage
        localStorage.setItem('my-tc-incident-reports-date-range', JSON.stringify(this.dateRange));
      },
      deep: true
    },
    filters: {
      handler: function () {
        this.loadIncidentReports();
        // Save to local storage
        localStorage.setItem('my-tc-incident-reports-filters', JSON.stringify(this.filters));
      },
      deep: true
    }
  },

  created() {
    this.setup();
  }
}
</script>

<style scoped>

</style>
